import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to always include cookies (httpOnly auth)
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Restore session from httpOnly cookie (no localStorage token needed)
    const restoreSession = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        if (response.data.preferred_language) {
          localStorage.setItem('preferredLanguage', response.data.preferred_language);
        }
      } catch {
        // No valid session cookie — user not logged in
        setUser(null);
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      });
      const { user: userData } = response.data;
      setUser(userData);
      if (userData.preferred_language) {
        localStorage.setItem('preferredLanguage', userData.preferred_language);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { user: newUser } = response.data;
      setUser(newUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Even if server logout fails, clear local state
    }
    setUser(null);
    localStorage.removeItem('preferredLanguage');
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data.preferred_language) {
        localStorage.setItem('preferredLanguage', response.data.preferred_language);
      }
      return response.data;
    } catch {
      return null;
    }
  };

  const isAuthenticated = !!user;

  // token is kept for backward compatibility with components that destructure it
  // Auth is now handled by httpOnly cookies sent automatically via withCredentials
  const token = isAuthenticated ? 'cookie-auth' : null;

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, login, register, logout, updateUser, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
